import * as React from 'react';
import {
  Box, Paper, Typography, Stack, IconButton, Button,
  TextField, MenuItem, Select, FormControl, InputLabel, 
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon   from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  useLazyFetchClubUsersQuery,
  useAddClubUsersMutation,
  useRmvClubUsersMutation,
  useUpdateClubUsersMutation,
} from "../../store/club/ClubApi";

const ROLES = [
  { value: 'contact',     label: 'Vor-Ort AnsprechpartnerIn' },
  { value: 'supervisor',  label: 'BetreuerIn'                },
  { value: 'responsible', label: 'TrainerIn'                 },
];

const RESPONSIBLE_FOR = [
  { value:  0, label: 'Alle Teams'       },
  { value: 10, label: 'weibl. Teams'     },
  { value: 11, label: 'wU8'              },
  { value: 12, label: 'wU10'             },
  { value: 20, label: 'männl. Teams'     },
  { value: 21, label: 'mU8'              },
  { value: 22, label: 'mU10'             },
  { value:  1, label: 'Altersgruppe U8'  },
  { value:  2, label: 'Altersgruppe U10' },
];

const EMPTY_ROW = (year, clubId) => ({
  id: Math.floor(Math.random() * 128000 + 50),
  email: '', first_name: '', last_name: '', mobile: '',
  role: 'contact', responsible_for: 0,
  year, clubId, isNew: true,
});

function mapPayload(payload) {
  return {
    id:             payload.user.id,
    email:          payload.user.email,
    first_name:     payload.user.first_name,
    last_name:      payload.user.last_name,
    mobile:         payload.user.mobile,
    role:           payload.role,
    responsible_for:payload.responsible_for,
    year:           payload.tournament.year,
    clubId:         payload.club.id,
    isNew:          false,
  };
}


function ContactRow({ row, onEdit, onDelete }) {
  const roleName = ROLES.find(r => r.value === row.role)?.label ?? row.role;
  const forName  = RESPONSIBLE_FOR.find(r => r.value === row.responsible_for)?.label ?? '';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 2, py: 1,
      '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {row.first_name} {row.last_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {row.email}{row.mobile ? ` · ${row.mobile}` : ''}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {roleName}{forName ? ` · ${forName}` : ''}
      </Typography>
      <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon fontSize="small" /></IconButton>
    </Box>
  );
}


function ContactEditForm({ row, onSave, onCancel, isLoading }) {
  const [form, setForm] = React.useState(row);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="Vorname"  value={form.first_name} onChange={set('first_name')} sx={{ flex: 1 }} />
          <TextField size="small" label="Nachname" value={form.last_name}  onChange={set('last_name')}  sx={{ flex: 1 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="E-Mail"  value={form.email}  onChange={set('email')}  sx={{ flex: 2 }} />
          <TextField size="small" label="Telefon" value={form.mobile} onChange={set('mobile')} sx={{ flex: 1 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Rolle</InputLabel>
            <Select value={form.role} label="Rolle" onChange={set('role')}>
              {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Verantwortlich für</InputLabel>
            <Select value={form.responsible_for} label="Verantwortlich für" onChange={(e) => setForm(f => ({ ...f, responsible_for: e.target.value }))}>
              {RESPONSIBLE_FOR.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={onCancel}>
            Abbrechen
          </Button>
          <Button size="small" variant="contained" startIcon={<SaveIcon />}
            onClick={() => onSave(form)} disabled={isLoading}>
            Speichern
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}


export function ClubUsers({ year, club }) {
  const [rows, setRows]           = React.useState([]);
  const [editingId, setEditingId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const [fetchClubUsers]  = useLazyFetchClubUsersQuery();
  const [addClubUsers]    = useAddClubUsersMutation();
  const [updateClubUsers] = useUpdateClubUsersMutation();
  const [rmvClubUsers]    = useRmvClubUsersMutation();

  React.useEffect(() => {
    fetchClubUsers({ year, id: club.id })
      .unwrap()
      .then(data => setRows(data.map(({ user, tournament, club, role, responsible_for }) => ({
        id: user.id, email: user.email,
        first_name: user.first_name, last_name: user.last_name,
        mobile: user.mobile, role, responsible_for,
        year: tournament.year, clubId: club.id, isNew: false,
      }))))
      .catch(console.error);
  }, [year, club.id, fetchClubUsers]);

  const handleAdd = () => {
    const newRow = EMPTY_ROW(year, club.id);
    setRows(r => [...r, newRow]);
    setEditingId(newRow.id);
  };

  const handleSave = async (form) => {
    setIsLoading(true);
    try {
      const payload = form.isNew
        ? await addClubUsers(form).unwrap()
        : await updateClubUsers(form).unwrap();
      const updated = mapPayload(payload);
      setRows(r => r.map(row => row.id === form.id ? updated : row));
      setEditingId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const row = rows.find(r => r.id === id);
    try {
      await rmvClubUsers({ year: row.year, clubId: row.clubId, userId: row.id }).unwrap();
      setRows(r => r.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = (id) => {
    const row = rows.find(r => r.id === id);
    if (row?.isNew) setRows(r => r.filter(r => r.id !== id));
    setEditingId(null);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 2, py: 1, bgcolor: 'grey.50',
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
          Kontakte
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          Hinzufügen
        </Button>
      </Box>

      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Noch keine Kontakte hinterlegt.
        </Typography>
      )}

      {rows.map(row => (
        editingId === row.id ? (
          <ContactEditForm
            key={row.id} row={row}
            onSave={handleSave}
            onCancel={() => handleCancel(row.id)}
            isLoading={isLoading}
          />
        ) : (
          <ContactRow
            key={row.id} row={row}
            onEdit={() => setEditingId(row.id)}
            onDelete={() => handleDelete(row.id)}
          />
        )
      ))}
    </Paper>
  );
}